import { useMemo } from "react";
import NeshanMap from "../../components/Map/NeshanMap";
import { toPersianNumbers } from "../../utils/toPersianNumbers";

const isValidCoordinate = (value) => {
  return Number.isFinite(Number(value));
};

const hasValidCoordinates = (latitude, longitude) => {
  return isValidCoordinate(latitude) && isValidCoordinate(longitude);
};

export default function ViewAddressModal({ address, onClose }) {
  const mapCenter = useMemo(() => {
    if (!address) return null;

    const lat = Number(address.latitude);
    const lng = Number(address.longitude);

    if (hasValidCoordinates(lat, lng)) {
      // Neshan / Mapbox expects [lng, lat]
      return [lng, lat];
    }

    return null;
  }, [address?.latitude, address?.longitude]);

  const addressTypeInfo = useMemo(() => {
    if (!address) return { text: "نامشخص", color: "bg-gray-100 text-gray-800" };

    switch (address.address_type) {
      case "customer":
        return { text: "مشتری", color: "bg-green-100 text-green-800" };
      case "warehouse":
        return { text: "انبار", color: "bg-blue-100 text-blue-800" };
      default:
        return {
          text: address.address_type || "نامشخص",
          color: "bg-gray-100 text-gray-800",
        };
    }
  }, [address?.address_type]);

  if (!address) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4">
      <div className="relative my-auto w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
        <button
          onClick={onClose}
          className="absolute left-4 top-4 z-10 text-gray-400 transition-colors hover:text-gray-600"
          aria-label="بستن"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="px-8 py-6">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-800">جزئیات آدرس</h2>
            <span className={`rounded-full px-3 py-1 text-xs font-bold ${addressTypeInfo.color}`}>
              {addressTypeInfo.text}
            </span>
          </div>

          <div className="mb-5 rounded-xl border border-gray-100 bg-gray-50 p-5">
            <div className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
              <div className="space-y-3">
                <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                  <span className="text-xs font-bold text-gray-500">عنوان</span>
                  <span className="text-sm font-medium text-gray-800">{address.title}</span>
                </div>

                <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                  <span className="text-xs font-bold text-gray-500">استان</span>
                  <span className="text-sm font-medium text-gray-800">
                    {address.province_detail || address.province_name || "---"}
                  </span>
                </div>

                <div className="flex items-start justify-between border-b border-gray-200 pb-2">
                  <span className="text-xs font-bold text-gray-500">آدرس</span>
                  <span className="max-w-[200px] text-left text-sm font-medium leading-relaxed text-gray-800">
                    {address.address_text || "---"}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                  <span className="text-xs font-bold text-gray-500">شهر</span>
                  <span className="text-sm font-medium text-gray-800">
                    {address.city_detail || address.city_name || "---"}
                  </span>
                </div>

                <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                  <span className="text-xs font-bold text-gray-500">کد پستی</span>
                  <span className="text-sm font-medium tracking-widest text-gray-800" dir="ltr">
                    {toPersianNumbers(address.postal_code) || "---"}
                  </span>
                </div>

                <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                  <span className="text-xs font-bold text-gray-500">وضعیت</span>
                  <div className="flex items-center gap-2">
                    <span
                      className={`h-2 w-2 rounded-full ${
                        address.is_active ? "bg-green-500" : "bg-red-500"
                      }`}
                    />
                    <span className="text-sm font-medium text-gray-800">
                      {address.is_active ? "فعال" : "غیرفعال"}
                    </span>
                  </div>
                </div>

                {address.is_default && (
                  <div className="flex justify-end pt-1">
                    <span className="rounded-md border border-yellow-200 bg-yellow-100 px-2 py-1 text-[10px] font-bold text-yellow-800">
                      آدرس پیش‌فرض
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mb-5 relative block h-64 w-full overflow-hidden rounded-xl border border-gray-200 shadow-sm">
            {mapCenter ? (
              <NeshanMap
                key={`view-map-${address.id}`}
                center={mapCenter}
                zoom={15}
                readOnly={true}
                className="h-full w-full"
              />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center space-y-2 rounded-xl border border-dashed border-gray-300 bg-gray-50">
                <svg className="h-8 w-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span className="text-xs font-medium text-gray-400">
                  موقعیت روی نقشه ثبت نشده است
                </span>
              </div>
            )}
          </div>

          {mapCenter && (
            <div className="mb-6 grid grid-cols-2 gap-4">
              <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-2 text-center">
                <p className="mb-0.5 text-[10px] font-bold text-blue-600">عرض جغرافیایی (Latitude)</p>
                <p className="font-mono text-sm text-blue-900">{Number(address.latitude).toFixed(6)}</p>
              </div>
              <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-2 text-center">
                <p className="mb-0.5 text-[10px] font-bold text-blue-600">طول جغرافیایی (Longitude)</p>
                <p className="font-mono text-sm text-blue-900">{Number(address.longitude).toFixed(6)}</p>
              </div>
            </div>
          )}

          <div className="flex justify-start">
            <button
              onClick={onClose}
              className="rounded-lg bg-gray-100 px-8 py-2 text-sm font-bold text-gray-600 transition-colors hover:bg-gray-200"
            >
              بستن صفحه
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
